import { customerBrowserHeaders, readCustomerSession, refreshCustomerSession, writeCustomerSession } from './customerSession';

type Json = Record<string, unknown> | unknown[];

const originalFetch = window.fetch.bind(window);
let installed = false;
let nextMessageId = 1;
const conversationToLegacy = new Map<string, number>();
const conversationFromLegacy = new Map<number, string>();
const messageToLegacy = new Map<string, number>();
const messageFromLegacy = new Map<number, string>();
const lastMessageByConversation = new Map<string, string>();
const orderVersions = new Map<string, number>();
const bookingClients = new Map<string, string>();
let ownerStorePromise: Promise<any> | null = null;
const asFlag = (value: unknown) => value === true || value === 1 || value === '1';

const vietnamParts = (value: string) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Ho_Chi_Minh', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(value));
  const get = (type: string) => parts.find((part) => part.type === type)?.value || '';
  return { date: `${get('year')}-${get('month')}-${get('day')}`, time: `${get('hour')}:${get('minute')}` };
};

const legacyStatus = (status: string) => status === 'completed' ? 'done'
  : ['travelling','met_customer'].includes(status) ? 'confirmed' : status;
const trackingStatus = (status: string) => ['travelling','met_customer'].includes(status) ? status : '';
const legacyOrder = (item: any, perspective: 'customer' | 'provider' = 'customer') => {
  const when = vietnamParts(item.startsAt);
  const customerView = perspective === 'customer';
  orderVersions.set(String(item.id), Number(item.version || 1));
  return {
    id: Number(item.id), backendId: Number(item.id), brand: 'massage', reference: item.orderCode || `TM${String(item.id).padStart(10,'0')}`,
    status: legacyStatus(String(item.status || 'selected')), trackingStatus: trackingStatus(String(item.status || '')),
    version: Number(item.version || 1), shop: item.partnerName || 'Chuyên viên Tìm Massage',
    service: item.serviceName || 'Dịch vụ massage', serviceId: Number(item.serviceId || 0), partnerId: Number(item.partnerId || 0),
    partnerUrl: item.partnerSlug ? `/shop/?partner=${encodeURIComponent(item.partnerSlug)}` : '',
    image: item.serviceImage || item.partnerImage || '', package: item.serviceName || '', minutes: Number(item.duration || 0),
    date: when.date, time: when.time, place: item.mode === 'mobile' ? 'Tại nhà' : 'Tại cửa hàng',
    address: item.address || '', lat: item.latitude == null ? null : Number(item.latitude), lng: item.longitude == null ? null : Number(item.longitude), mapUrl: item.mapUrl || '',
    name: item.customerName || '', phone: item.customerPhone || '', note: item.note || '',
    price: customerView ? Number(item.total || item.price || 0) : Number(item.price || 0), platformFee: Number(item.fee || 0),
    specialistReceivable: Number(item.price || 0), customerTotal: Number(item.total || 0), paymentStatus: item.paymentStatus || 'unpaid',
    expiresAt: item.acceptBy, acceptBy: item.acceptBy, createdAt: item.createdAt, serverTime: new Date().toISOString(),
    customerId: item.customerId, ownerId: item.ownerId, shopOrder: item.ownerId === readCustomerSession()?.globalUserId,
    selectedForCurrentUser: !customerView && item.status === 'selected',
    shopRejected: Boolean(item.shopRejected),
    contactLocked: Boolean(item.contactLocked),
    canContact: item.canContact === true, contactExpiresAt: item.contactExpiresAt || null,
    acceptedShop: { partnerId: Number(item.partnerId || 0), name: item.partnerName || '', avatar: item.partnerImage || '', phone: item.partnerPhone || '', rating: Number(item.partnerRating || 0), reviewCount: Number(item.partnerReviewCount || 0) },
    randomAssignment: Boolean(item.randomProductId), randomProductId: Number(item.randomProductId || 0), randomVariantId: Number(item.randomVariantId || 0),
    randomTicket: item.randomTicket && typeof item.randomTicket === 'object' ? item.randomTicket : null,
    selectedApplicantId: item.selectedApplicantId || item.ownerId || '',
    preferredApplicantIds: Array.isArray(item.preferredApplicantIds) ? item.preferredApplicantIds : [],
    candidates: Array.isArray(item.candidates) ? item.candidates : [],
    regionalShops: Array.isArray(item.regionalShops) ? item.regionalShops : [],
  };
};

const stableClientId = (source: unknown) => {
  const key = String(source || '');
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(key)) return key;
  if (!bookingClients.has(key)) bookingClients.set(key, crypto.randomUUID());
  return bookingClients.get(key)!;
};

// `Number(null)` and `Number('')` are both zero. A missing map pin must not
// therefore turn into the real geographic point 0,0 when a legacy web form is
// bridged to the platform API.
const optionalCoordinate = (value: unknown, minimum: number, maximum: number) => {
  if (value === null || value === undefined || value === '') return null;
  const coordinate = typeof value === 'number' ? value : Number(String(value).trim());
  return Number.isFinite(coordinate) && coordinate >= minimum && coordinate <= maximum ? coordinate : null;
};

const numericId = (source: string | number, forward: Map<string,number>, reverse: Map<number,string>, next: () => number) => {
  const key = String(source);
  const existing = forward.get(key);
  if (existing) return existing;
  const id = next();
  forward.set(key, id); reverse.set(id, key);
  return id;
};
const legacyConversationId = (source: string | number) => {
  const key=String(source);const id=Number(key);
  if(!/^[1-9][0-9]*$/.test(key)||!Number.isSafeInteger(id))throw new Error('INVALID_CONVERSATION_ID');
  return id;
};
const legacyMessageId = (id: string | number) => numericId(id, messageToLegacy, messageFromLegacy, () => nextMessageId++);

const json = (value: Json, status = 200) => new Response(JSON.stringify(value), {
  status,
  headers: { 'Content-Type': 'application/json; charset=utf-8' },
});

async function platform(path: string, init: RequestInit = {}, authenticated = false, retry = true, transientRetry = true) {
  let session = readCustomerSession();
  if (authenticated && !session) session = await refreshCustomerSession();
  const headers = new Headers(init.headers || {});
  headers.set('X-Tim-Tenant', 'massage'); headers.set('X-Tim-Audience', 'tim-massage');
  if (authenticated && session?.accessToken) headers.set('Authorization', `Bearer ${session.accessToken}`);
  const method = String(init.method || 'GET').toUpperCase();
  let response: Response;
  try {
    response = await originalFetch(`/api${path}`, { ...init, credentials: 'same-origin', headers });
  } catch (error) {
    if (method === 'GET' && transientRetry) {
      await new Promise((resolve) => window.setTimeout(resolve, 300));
      return platform(path, init, authenticated, retry, false);
    }
    throw error;
  }
  if (method === 'GET' && transientRetry && response.status >= 500) {
    await new Promise((resolve) => window.setTimeout(resolve, 300));
    return platform(path, init, authenticated, retry, false);
  }
  if (response.status === 401 && authenticated && retry) {
    const refreshed = await refreshCustomerSession();
    if (refreshed) return platform(path, init, authenticated, false, transientRetry);
  }
  const body = await response.json().catch(() => null);
  if (response.status === 401 && authenticated) {
    writeCustomerSession(null);
    return {
      response,
      error: {
        code: 'SESSION_EXPIRED',
        message: 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại để tiếp tục.',
      },
    };
  }
  if (!response.ok) return { response, error: body?.error || { code: 'API_UNAVAILABLE' } };
  return { response, data: body?.data };
}

// Legacy chat routes expose compact numeric ids to the existing UI. The maps
// live in memory, so a direct conversation URL (or a page reload) must rebuild
// them before resolving that numeric id.
async function originalConversationId(legacyId: number) {
  return Number.isSafeInteger(legacyId)&&legacyId>0?String(legacyId):null;
}

const legacyErrorMessages: Record<string, string> = {
  SELF_BOOKING_DENIED: 'Tài khoản đang gửi đơn là chủ gian hàng này. Vui lòng đăng nhập tài khoản khách khác. Đổi tên hoặc số liên hệ trong đơn không đổi tài khoản đăng nhập.',
  CALL_USE_CMS: 'Bạn đang dùng tài khoản tổng đài. Hãy gọi khách từ CMS; để gọi đến tổng đài, hãy đăng nhập tài khoản khách khác.',
  ORDER_CONTACT_NOT_ACCEPTED: 'Chỉ được liên hệ sau khi chuyên viên nhận đơn.',
  ORDER_CONTACT_CLOSED: 'Đơn đã hủy hoặc đã hết thời gian liên hệ.',
  ORDER_CONTACT_FORBIDDEN: 'Bạn không phải người tham gia đơn này.',
  CALL_SELF_NOT_ALLOWED: 'Không thể gọi cho chính tài khoản đang đăng nhập.',
  STORE_NOT_APPROVED: 'Gian hàng cần được duyệt trước khi bật nhận đơn.',
  STORE_HAS_NO_ACTIVE_SERVICE: 'Hãy thêm và bật ít nhất một dịch vụ đã duyệt trước khi nhận đơn.',
  ORDER_ACCEPT_EXPIRED: 'Đã quá thời gian xác nhận đơn. Bạn không thể nhận đơn này nữa.',
  OUT_OF_SERVICE_AREA: 'Vị trí này nằm ngoài phạm vi nhận đơn của chuyên viên. Vui lòng chọn chuyên viên gần hơn hoặc đổi vị trí.',
  EXCLUDED_SERVICE_AREA: 'Vị trí này nằm trong khu vực chuyên viên không nhận đơn. Vui lòng chọn chuyên viên khác hoặc đổi vị trí.',
  LOCATION_REQUIRED: 'Hãy chọn vị trí chính xác trên bản đồ trước khi đặt đơn.',
  SESSION_EXPIRED: 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại để tiếp tục.',
};

const legacyError = (result: any) => {
  if (['ORDER_CONTACT_CLOSED','ORDER_CONTACT_FORBIDDEN'].includes(result.error?.code)) window.dispatchEvent(new CustomEvent('tm:order-contact-closed'));
  return json({
  code: result.error?.code || 'API_UNAVAILABLE',
  message: legacyErrorMessages[result.error?.code] || result.error?.message || 'Không thể hoàn tất yêu cầu.',
}, result.response?.status || 500);
};

const parseBody = (init?: RequestInit) => {
  try { return init?.body ? JSON.parse(String(init.body)) : {}; } catch { return {}; }
};

const chatMessage = (item: any) => ({
  id: legacyMessageId(item.id),
  // Node resolves direction against the authenticated participant. Keep the
  // session comparison only as a compatibility fallback for older responses.
  sender_id: item.direction === 'outgoing'
    ? 1
    : item.direction === 'incoming'
      ? 2
      : String(item.senderId) === String(readCustomerSession()?.globalUserId || '') ? 1 : 2,
  type: item.messageType || 'text',
  content: item.body || '',
  created_at: item.createdAt || new Date().toISOString(),
  status: item.readAt ? 'read' : 'sent',
  read_at: item.readAt || null,
});

const callHistoryMessage = (item: any) => {
  const selfId = String(readCustomerSession()?.globalUserId || '');
  const outgoing = String(item.callerId || '') === selfId;
  const status = String(item.status || '').toLowerCase();
  const reason = String(item.endReason || '').toLowerCase();
  const connected = Boolean(item.connectedAt || status === 'connected');
  const declined = ['declined', 'rejected', 'busy'].includes(reason) || status === 'declined';
  const missed = !connected && (['expired', 'timeout', 'no_answer', 'missed'].includes(reason) || status === 'expired');
  const endedAt = item.endedAt ? new Date(item.endedAt).getTime() : 0;
  const connectedAt = item.connectedAt ? new Date(item.connectedAt).getTime() : 0;
  return {
    id: legacyMessageId(`call:${item.id}`),
    sender_id: outgoing ? 1 : 2,
    type: 'call',
    content: missed ? 'Cuộc gọi nhỡ' : declined ? 'Cuộc gọi đã từ chối' : connected ? 'Cuộc gọi đã kết thúc' : 'Cuộc gọi',
    created_at: item.createdAt || new Date().toISOString(),
    status: status || 'ended',
    call: {
      id: item.id,
      direction: outgoing ? 'outgoing' : 'incoming',
      durationMs: connectedAt && endedAt ? Math.max(0, endedAt - connectedAt) : 0,
      endReason: reason,
    },
  };
};

const category = (item: any) => ({
  id: item.id,
  slug: item.slug || String(item.id),
  name: item.name,
  image: item.image || '',
  link: `/chuyen-vien/?service=${encodeURIComponent(item.slug || String(item.id))}`,
});

const groupedPartnerServices = (services: any[]) => {
  const groups = new Map<string, any>();
  for (const service of services) {
    const key = [service.name, service.description, service.image, service.mode, service.category]
      .map((value) => String(value || '').trim().toLowerCase())
      .join('\u0000');
    const packageItem = {
      id: service.id,
      name: service.packageName || (Number(service.duration) > 0 ? `${Number(service.duration)} phút` : service.name),
      description: Number(service.duration) > 0 ? `${Number(service.duration)} phút` : (service.description || ''),
      price: Number(service.price || 0),
      duration: Number(service.duration || 0),
    };
    const existing = groups.get(key);
    if (existing) {
      existing.packages.push(packageItem);
      continue;
    }
    groups.set(key, {
      ...service,
      category: service.category || '',
      packages: Array.isArray(service.packages) && service.packages.length
        ? service.packages
        : [packageItem],
    });
  }
  return [...groups.values()];
};

const partner = (item: any) => ({
  id: item.id,
  slug: item.slug,
  name: item.name,
  intro: item.intro || '',
  shortDescription: item.intro || '',
  image: item.image || '',
  avatar: item.image || '',
  cover: item.image || '',
  gallery: Array.isArray(item.gallery) ? item.gallery : [],
  address: item.address || '',
  accepting: Boolean(item.accepting),
  acceptOrders: Boolean(item.accepting),
  serviceMode: item.serviceMode || 'both',
  lat: item.latitude == null ? null : Number(item.latitude),
  lng: item.longitude == null ? null : Number(item.longitude),
  radiusKm: Number(item.radiusKm || 10),
  bookingPolicy: {
    leadMinutes: Number(item.bookingPolicy?.leadMinutes || 30),
    advanceBookingEnabled: item.bookingPolicy?.advanceBookingEnabled !== false,
    advanceBookingDays: Number(item.bookingPolicy?.advanceBookingDays || 7),
  },
  distance: item.distance == null ? null : Number(item.distance),
  selectedServicePrice: item.selectedServicePrice == null ? null : Number(item.selectedServicePrice),
  rating: Number(item.rating || 0),
  ratingCount: Number(item.reviewCount || item.ratingCount || 0),
  priceFrom: Number(item.priceFrom || 0),
  categories: Array.isArray(item.categories) ? item.categories : [],
  services: Array.isArray(item.services) ? groupedPartnerServices(item.services) : [],
  url: `/shop/?partner=${encodeURIComponent(item.slug || String(item.id))}`,
  saved: Boolean(item.saved),
  // The reference layout still needs the source action strip to finish its
  // structural pass. The target-only cleanup removes that strip and every
  // derived contact surface immediately afterwards.
  contactBarEnabled: true,
});

const legacyOwnerStore = (item: any) => {
  const profileConfig = item?.profileConfig && typeof item.profileConfig === 'object' ? item.profileConfig : {};
  const media = Array.isArray(item.media) ? item.media.map((entry: any) => ({
    ...entry,
    mime: entry.mime || entry.mimeType || 'image/webp',
    type: entry.mediaType || 'gallery',
  })) : [];
  const ids = (type: string) => media.filter((entry: any) => entry.type === type).map((entry: any) => Number(entry.id));
  const selection = profileConfig.mediaSelection && typeof profileConfig.mediaSelection === 'object' ? profileConfig.mediaSelection : {};
  return {
    ...profileConfig,
    ...item,
    accepting: asFlag(item.accepting),
    image: item.image || '', avatar: item.image || '', cover: item.cover || '',
    avatarId: Number(selection.avatarId) || ids('avatar')[0] || 0,
    coverIds: selection.coverId ? [Number(selection.coverId)] : ids('cover'),
    albumIds: Array.isArray(selection.albumIds) ? selection.albumIds.map(Number).filter(Boolean) : ids('gallery'), videoIds: [],
    lat: item.latitude ?? null, lng: item.longitude ?? null,
    status: item.moderationStatus || item.status || 'unknown',
    url: `/shop/${encodeURIComponent(item.slug || String(item.id))}/`,
    serviceMode: profileConfig.serviceMode || 'both', storeType: profileConfig.storeType || 'studio',
    radiusKm: Number(profileConfig.radiusKm || 10), coverageMode: profileConfig.coverageMode || 'areas',
    areas: Array.isArray(profileConfig.areas) ? profileConfig.areas : [], areaLabels: profileConfig.areaLabels || {},
    hours: profileConfig.hours || '', amenities: Array.isArray(profileConfig.amenities) ? profileConfig.amenities : [],
    highlights: Array.isArray(profileConfig.highlights) ? profileConfig.highlights : [],
    zalo: profileConfig.zalo || '', whatsapp: profileConfig.whatsapp || '', telegram: profileConfig.telegram || '',
    facebook: profileConfig.facebook || '', tiktok: profileConfig.tiktok || '', instagram: profileConfig.instagram || '',
    shopee: profileConfig.shopee || '', mapUrl: profileConfig.mapUrl || '', chatEnabled: profileConfig.chatEnabled !== false,
    media,
    services: Array.isArray(item.services) ? item.services.map((service: any) => ({
      ...service,
      category: service.category || '',
      packages: Array.isArray(service.packages) && service.packages.length ? service.packages.map((entry: any) => ({
        ...entry,
        name: Number(entry.duration) > 0 ? `${Number(entry.duration)} phút` : (entry.name || entry.label || ''),
        description: Number(entry.duration) > 0 ? `${Number(entry.duration)} phút` : (entry.description || ''),
      })) : [{
        name: `${Number(service.duration) || 60} phút`, description: `${Number(service.duration) || 60} phút`,
        duration: Number(service.duration) || 60, price: Number(service.price) || 0, fee: Number(service.fee) || 0,
        active: true, sortOrder: 0,
      }],
      media: service.image ? [{ id: `service-${service.id}`, url: service.image, mime: 'image/webp' }] : [],
      pricing: service.pricing && typeof service.pricing === 'object' ? service.pricing : {},
    })) : [],
  };
};

async function ownerStore(partnerId = '') {
  ownerStorePromise = null;
  if (!ownerStorePromise) ownerStorePromise = (async () => {
    const stores = await platform('/v1/sites/massage/owner/stores', {}, true);
    if (!stores.data) return stores;
    const items = Array.isArray(stores.data.items) ? stores.data.items : [];
    const selected = items.find((item: any) => String(item.id) === String(partnerId)) || items[0];
    if (!selected) return { response: new Response(null, { status: 404 }), error: { code: 'STORE_NOT_FOUND', message: 'Tài khoản chưa có gian hàng.' } };
    return platform(`/v1/sites/massage/owner/stores/${selected.id}`, {}, true);
  })();
  const result = await ownerStorePromise;
  return result.data ? { ...result, data: legacyOwnerStore(result.data) } : result;
}

async function saveOwnerStore(payload: Record<string, any>, partnerId = '') {
  const currentResult = await ownerStore(partnerId);
  if (!currentResult.data) return currentResult;
  const current = currentResult.data;
  const coreKeys = new Set(['id', 'partnerId', 'expectedVersion', 'version', 'name', 'intro', 'shortDescription', 'address', 'phone', 'email', 'accepting', 'lat', 'lng', 'latitude', 'longitude']);
  const profilePatch = Object.fromEntries(Object.entries(payload).filter(([key]) => !coreKeys.has(key)));
  const profileConfig = {
    ...(current.profileConfig && typeof current.profileConfig === 'object' ? current.profileConfig : {}),
    ...profilePatch,
  };
  const result = await platform(`/v1/sites/massage/owner/stores/${encodeURIComponent(String(current.id))}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      expectedVersion: Number(current.version),
      name: String(payload.name ?? current.name ?? '').trim(),
      intro: String(payload.intro ?? payload.shortDescription ?? current.intro ?? ''),
      address: String(payload.address ?? current.address ?? '').trim(),
      phone: String(payload.phone ?? current.phone ?? ''),
      email: String(payload.email ?? current.email ?? ''),
      accepting: payload.accepting === undefined ? Boolean(current.accepting) : Boolean(payload.accepting),
      latitude: payload.lat ?? payload.latitude ?? current.lat ?? current.latitude ?? null,
      longitude: payload.lng ?? payload.longitude ?? current.lng ?? current.longitude ?? null,
      profileConfig,
    }),
  }, true);
  if (result.data) ownerStorePromise = null;
  return result.data ? { ...result, data: legacyOwnerStore(result.data) } : result;
}

async function saveOwnerAccepting(accepting: boolean, partnerId = '') {
  const attempt = async (retryOnConflict: boolean): Promise<any> => {
    const currentResult = await ownerStore(partnerId);
    if (!currentResult.data) return currentResult;
    const current = currentResult.data;
    const result = await platform(`/v1/sites/massage/owner/stores/${encodeURIComponent(String(current.id))}/accepting`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ expectedVersion: Number(current.version), accepting }),
    }, true);
    if (result.data) {
      ownerStorePromise = null;
      return { ...result, data: legacyOwnerStore(result.data) };
    }
    if (result.error?.code === 'STORE_VERSION_CONFLICT') {
      ownerStorePromise = null;
      if (retryOnConflict) return attempt(false);
    }
    return result;
  };
  return attempt(true);
}

async function saveOwnerMediaSelection(payload: Record<string, any>, partnerId = '') {
  const currentResult = await ownerStore(partnerId);
  if (!currentResult.data) return currentResult;
  const current = currentResult.data;
  const result = await platform(`/v1/sites/massage/owner/stores/${encodeURIComponent(String(current.id))}/media-selection`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      expectedVersion: Number(current.version),
      avatarId: Number(payload.avatarId) || null,
      coverId: Number(payload.coverIds?.[0]) || null,
      albumIds: Array.isArray(payload.albumIds) ? payload.albumIds.map(Number).filter(Boolean).slice(0, 50) : [],
    }),
  }, true);
  if (result.data) ownerStorePromise = null;
  return result.data ? { ...result, data: legacyOwnerStore(result.data) } : result;
}

const serviceInput = (payload: Record<string, any>, current: any = {}) => {
  const packages = Array.isArray(payload.packages) ? payload.packages : current.packages || [];
  const first = packages.find((item: any) => item.active !== false) || packages[0] || {};
  const durationText = String(payload.pricing?.duration || '').match(/\d+/)?.[0];
  const mediaIds = Array.isArray(payload.mediaIds) ? payload.mediaIds.map(Number).filter(Boolean) : [];
  return {
    name: String(payload.name ?? current.name ?? '').trim(),
    description: String(payload.description ?? current.description ?? ''),
    image: String(payload.image ?? current.image ?? ''),
    price: Number(payload.price ?? first.price ?? current.price ?? 0),
    fee: Number(payload.fee ?? current.fee ?? 0),
    duration: Math.max(10, Number(payload.duration ?? first.duration ?? durationText ?? current.duration ?? 60)),
    mode: payload.mode === 'at_store' || current.mode === 'at_store' ? 'at_store' : 'mobile',
    active: payload.active === undefined ? current.active !== false : payload.active !== false,
    sortOrder: Number(payload.sortOrder ?? current.sortOrder ?? 0),
    category: String(payload.category ?? current.category ?? ''),
    pricing: payload.pricing && typeof payload.pricing === 'object' ? payload.pricing : current.pricing || {},
    packages: packages.map((item: any, index: number) => ({
      name: String(item.name || item.label || `${Number(item.duration) || 60} phút`),
      description: String(item.description || ''), duration: Math.max(10, Number(item.duration || durationText || 60)),
      price: Number(item.price || 0), fee: Number(item.fee || 0), active: item.active !== false, sortOrder: index,
    })),
    mediaIds,
  };
};

async function saveOwnerService(payload: Record<string, any>, serviceId = '') {
  const currentResult = await ownerStore();
  if (!currentResult.data) return currentResult;
  const store = currentResult.data;
  const current = serviceId ? store.services.find((item: any) => String(item.id) === String(serviceId)) : null;
  if (serviceId && !current) return { response: new Response(null, { status: 404 }), error: { code: 'SERVICE_NOT_FOUND', message: 'Không tìm thấy dịch vụ.' } };
  const input = serviceInput(payload, current || {});
  if (input.mediaIds.length) {
    const selected = store.media.find((item: any) => input.mediaIds.includes(Number(item.id)));
    if (selected?.url) input.image = selected.url;
  }
  const path = serviceId ? `/v1/sites/massage/owner/services/${encodeURIComponent(serviceId)}` : '/v1/sites/massage/owner/services';
  const result = await platform(path, {
    method: serviceId ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(serviceId ? { ...input, expectedVersion: Number(current.version), mediaIds: undefined } : { ...input, partnerId: String(store.id), mediaIds: undefined }),
  }, true);
  if (result.data) ownerStorePromise = null;
  return result;
}

async function uploadOwnerMedia(form: FormData) {
  const file = form.get('file');
  if (!(file instanceof File)) return { response: new Response(null, { status: 422 }), error: { code: 'MEDIA_FILE_REQUIRED', message: 'Hãy chọn ảnh cần tải.' } };
  if (!['image/jpeg','image/png','image/webp'].includes(file.type)) return { response: new Response(null, { status: 415 }), error: { code: 'MEDIA_TYPE_INVALID', message: 'Bản Node nhận ảnh JPG, PNG hoặc WebP.' } };
  const storeResult = await ownerStore();
  if (!storeResult.data && storeResult.error?.code !== 'STORE_NOT_FOUND') return storeResult;
  const purpose = String(form.get('purpose') || 'image');
  if (!storeResult.data) {
    const uploaded = await platform('/v1/sites/massage/owner/application/media', {
      method: 'POST', headers: { 'Content-Type': file.type, 'X-Tim-Media-Purpose': purpose }, body: file,
    }, true);
    return uploaded.data ? { ...uploaded, data: { ...uploaded.data, mime: file.type } } : uploaded;
  }
  const mediaType = purpose === 'avatar' ? 'avatar' : purpose === 'cover' ? 'cover' : purpose === 'service' ? 'service' : 'gallery';
  const ticket = await platform('/v1/sites/massage/owner/media/uploads', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ partnerId: String(storeResult.data.id), fileName: file.name, mimeType: file.type, size: file.size, mediaType, altText: storeResult.data.name }),
  }, true);
  if (!ticket.data) return ticket;
  const uploaded = await platform(ticket.data.uploadUrl.replace(/^\/api/, ''), { method: 'PUT', headers: ticket.data.headers, body: file }, true);
  if (uploaded.data) ownerStorePromise = null;
  return uploaded.data ? { ...uploaded, data: { ...uploaded.data, id: uploaded.data.id || uploaded.data.mediaId || ticket.data.mediaId, mime: file.type } } : uploaded;
}

export function installLegacyUiAdapter() {
  if (installed) return;
  installed = true;
  // The shared image editor uses XHR for progress. Route its Node uploads
  // through the same authenticated VPS adapter as fetch-based editors.
  (window as any).TimVpsOwnerUpload = async (file: File, purpose: string) => {
    if (file.size > 10485760) throw new Error('Ảnh vượt quá 10 MB. Hãy chọn ảnh nhỏ hơn.');
    const form = new FormData();
    form.append('file', file); form.append('purpose', purpose);
    const result = await uploadOwnerMedia(form);
    if (!result.data) throw new Error(result.error?.message || 'Không thể tải ảnh lên VPS.');
    return result.data;
  };
  let pendingOtp: { phone: string; challenge: string; code: string; purpose: string } | null = null;
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const requestUrl = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const url = new URL(requestUrl, location.origin);
    const mobilePrefix = '/node-compat/mobile/v2/';
    if (url.origin === location.origin && url.pathname.startsWith(mobilePrefix)) {
      const route = url.pathname.slice(mobilePrefix.length).replace(/^\/+|\/+$/g, '');
      const payload = parseBody(init);
      if (route === 'auth/otp/request') {
        const result = await platform('/v1/auth/otp/request', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: String(payload.phone || ''), tenantId: 'massage', purpose: String(payload.purpose || 'login') }),
        });
        pendingOtp = null;
        return result.data ? json(result.data) : legacyError(result);
      }
      if (route === 'auth/otp/verify') {
        pendingOtp = {
          phone: String(payload.phone || ''), challenge: String(payload.challenge || ''),
          code: String(payload.code || ''), purpose: String(payload.purpose || 'login'),
        };
        return json({ proof: 'node-complete-required' });
      }
      if (route === 'auth/otp/complete') {
        if (!pendingOtp || pendingOtp.phone !== String(payload.phone || '')) {
          return json({ code: 'OTP_CHALLENGE_MISSING', message: 'Phiên xác minh OTP đã hết hạn. Vui lòng gửi lại mã.' }, 409);
        }
        const response = await originalFetch('/api/v1/auth/otp/complete', {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', ...customerBrowserHeaders },
          body: JSON.stringify({ ...pendingOtp, tenantId: 'massage', audience: 'tim-massage', deviceId: `customer-web-${crypto.randomUUID()}` }),
        });
        const envelope = await response.json().catch(() => null);
        pendingOtp = null;
        if (!response.ok || !envelope?.data?.accessToken) return json({ code: envelope?.error?.code || 'OTP_INVALID', message: envelope?.error?.message || 'Mã OTP không hợp lệ.' }, response.status);
        return json(envelope.data, response.status);
      }
      if (route === 'auth/reviewer-login') {
        const response = await originalFetch('/api/v1/auth/password/login', {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', ...customerBrowserHeaders },
          body: JSON.stringify({ identifier: String(payload.phone || ''), password: String(payload.password || ''), tenantId: 'massage', audience: 'tim-massage', deviceId: `reviewer-web-${crypto.randomUUID()}` }),
        });
        const envelope = await response.json().catch(() => null);
        return response.ok && envelope?.data?.accessToken ? json(envelope.data, response.status) : json({ code: envelope?.error?.code || 'CREDENTIALS_INVALID', message: envelope?.error?.message || 'Thông tin đăng nhập không đúng.' }, response.status);
      }
      if (route === 'auth/web-session') {
        const headers = new Headers(init?.headers || {});
        const accessToken = headers.get('Authorization')?.replace(/^Bearer\s+/i, '') || '';
        if (!accessToken) return json({ code: 'SESSION_TOKEN_MISSING', message: 'Không tạo được phiên đăng nhập.' }, 401);
        writeCustomerSession({ accessToken });
        const allowed = new Set(['trang-chu','don-hang','cong-dong','tin-nhan','ca-nhan','tai-khoan','cua-hang','quan-ly-gian-hang']);
        const destination = allowed.has(String(payload.destination || '')) ? String(payload.destination) : 'tai-khoan';
        return json({ url: `${location.origin}/${destination}/` });
      }
      return json({ code: 'NOT_IMPLEMENTED', message: 'Chức năng đăng nhập này chưa được hỗ trợ.' }, 501);
    }
    if (url.origin !== location.origin || !url.pathname.startsWith('/legacy/')) return originalFetch(input, init);
    const route = url.pathname.slice('/legacy/'.length).replace(/^\/+|\/+$/g, '');
    if (route === 'auth/login') {
      const payload = parseBody(init);
      const response = await originalFetch('/api/v1/auth/password/login', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', ...customerBrowserHeaders },
        body: JSON.stringify({
          identifier: String(payload.identifier || payload.phone || ''), password: String(payload.password || ''),
          tenantId: 'massage', audience: 'tim-massage', deviceId: `customer-web-${crypto.randomUUID()}`,
        }),
      });
      const envelope = await response.json().catch(() => null);
      if (!response.ok || !envelope?.data?.accessToken) return json({ code: envelope?.error?.code || 'CREDENTIALS_INVALID', message: envelope?.error?.message || 'Không thể đăng nhập.' }, response.status);
      writeCustomerSession(envelope.data);
      const redirect = typeof payload.redirect_to === 'string' && payload.redirect_to.startsWith(location.origin)
        ? payload.redirect_to : `${location.origin}/ca-nhan/`;
      return json({ redirect });
    }
    if (route === 'auth/register') {
      const payload=parseBody(init),phone=String(payload.phone||'').replace(/\D/g,''),challenge=String(payload.challenge||''),code=String(payload.code||'').trim();
      if(!challenge){
        const requested=await platform('/v1/auth/otp/request',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({phone,tenantId:'massage',purpose:'register'})});
        if(!requested.data)return legacyError(requested);
        return json({requiresOtp:true,challenge:requested.data.challenge,expiresIn:requested.data.expiresIn||300,retryAfter:requested.data.retryAfter||60});
      }
      const registered=await originalFetch('/api/v1/auth/register/phone',{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json',...customerBrowserHeaders},body:JSON.stringify({phone,challenge,code,displayName:String(payload.name||'').trim(),password:String(payload.password||''),tenantId:'massage',audience:'tim-massage',deviceId:`customer-web-${crypto.randomUUID()}`})});
      const envelope=await registered.json().catch(()=>null);
      if(!registered.ok||!envelope?.data?.accessToken){const code=envelope?.error?.code||'REGISTRATION_FAILED';return json({code,message:code==='ACCOUNT_EXISTS'?'Số điện thoại này đã có tài khoản. Hãy đăng nhập hoặc dùng Quên mật khẩu.':envelope?.error?.message||'Không thể hoàn tất đăng ký.'},registered.status);}
      writeCustomerSession(envelope.data);
      const redirect=typeof payload.redirect_to==='string'&&payload.redirect_to.startsWith(location.origin)?payload.redirect_to:`${location.origin}/ca-nhan/`;
      return json({redirect});
    }
    if (route === 'auth/logout') {
      const result = await platform('/v1/auth/revoke', { method: 'POST', headers: customerBrowserHeaders }, true);
      writeCustomerSession(null);
      return result.data ? json({ loggedOut: true }) : legacyError(result);
    }
    if (route === 'me') {
      const result = await platform('/v1/me', {}, true);
      if (!result.data) return legacyError(result);
      const item = result.data;
      const storeResult = await ownerStore();
      if (!storeResult.data && storeResult.response.status !== 404) return legacyError(storeResult);
      const ownedStore = storeResult?.data || null;
      const storeApproved = ownedStore?.status === 'approved';
      const hasActiveApprovedService = Boolean(ownedStore?.services?.some((service: any) => service.active && service.moderationStatus === 'approved'));
      const hasPendingService = Boolean(ownedStore?.services?.some((service: any) => service.moderationStatus === 'pending'));
      const hasApprovedService = Boolean(ownedStore?.services?.some((service: any) => service.moderationStatus === 'approved'));
      const acceptingBlockedReason = !storeApproved
        ? 'Gian hàng cần được duyệt trước khi bật nhận đơn.'
        : !hasActiveApprovedService
          ? hasPendingService ? 'Sản phẩm hoặc dịch vụ của bạn đang chờ duyệt. Vui lòng liên hệ tổng đài để được hỗ trợ kiểm duyệt.'
            : hasApprovedService ? 'Hãy bật hiển thị ít nhất một dịch vụ đã được duyệt để nhận đơn.'
            : 'Bạn cần thêm ít nhất một dịch vụ và được duyệt trước khi nhận đơn. Liên hệ tổng đài nếu cần hỗ trợ.'
          : '';
      return json({
        ...item, userId: 1, id: 1,
        name: item.displayName || 'Tài khoản Tìm Massage',
        avatar: ownedStore?.avatar || item.avatarUrl || '/massage-assets/logo.png',
        address: ownedStore?.address || item.address || '', roles: ownedStore ? ['partner'] : [],
        partnerId: Number(ownedStore?.id || 0), partnerStatus: ownedStore?.status || '',
        acceptOrders: ownedStore ? asFlag(ownedStore.accepting) : false,
        canAcceptOrders: Boolean(ownedStore && storeApproved && hasActiveApprovedService),
        acceptingBlockedReason,
        counts: {},
      });
    }
    if (route === 'specialist-wallet' && (init?.method || 'GET').toUpperCase() === 'GET') {
      const [result, paymentConfig] = await Promise.all([
        platform('/v1/sites/massage/partner-wallet?page=1', {}, true),
        platform('/v1/sites/massage/payment-config', {}, true),
      ]);
      if (!result.data) return legacyError(result);
      const items = Array.isArray(result.data.items) ? result.data.items : [];
      const configured = Boolean(paymentConfig.data?.ready);
      return json({
        brand: 'massage',
        balance: Number(result.data.balance || 0),
        transferReference: result.data.transferReference || '',
        debt: 0,
        feeBps: 0,
        feePercent: 0,
        entries: items.map((item: any) => ({
          id: item.id,
          amount: (item.direction === 'debit' ? -1 : 1) * Number(item.amount || 0),
          kind: item.entryType || 'wallet_adjustment',
          reference: item.referenceId || '',
          description: item.description || '',
          created_at: item.createdAt || '',
        })),
        topups: [],
        bank: {
          configured,
          qrConfigured: configured,
          bank_id: paymentConfig.data?.bankId || '',
          number: paymentConfig.data?.accountNumber || '',
          holder: paymentConfig.data?.accountHolder || '',
          transfer_prefix: paymentConfig.data?.transferPrefix || 'TIM',
        },
      });
    }
    if (route === 'specialist-wallet/topups' && (init?.method || 'GET').toUpperCase() === 'POST') {
      const payload = parseBody(init);
      const requestKey = String(payload.requestKey || '');
      const [created, paymentConfig] = await Promise.all([
        platform('/v1/sites/massage/partner-payment-intents', { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({clientId:requestKey}) }, true),
        platform('/v1/sites/massage/payment-config', {}, true),
      ]);
      if (!created.data) return legacyError(created);
      if (!paymentConfig.data?.ready) return json({code:'TOPUP_NOT_CONFIGURED',message:'VietQR chưa được cấu hình.'},503);
      const intent = created.data.intent;
      return json({
        id:intent.id, reference:intent.transferReference || intent.id, amount:0, status:intent.status || 'pending',
        bank:{configured:true,qrConfigured:true,bank_id:paymentConfig.data.bankId,number:paymentConfig.data.accountNumber,holder:paymentConfig.data.accountHolder,transfer_prefix:paymentConfig.data.transferPrefix},
      }, created.response.status === 201 ? 201 : 200);
    }
    if (route === 'calls/device-session') {
      const result = await platform('/v1/sites/massage/chat/calls/device-session', { method: 'POST' }, true);
      return result.data ? json(result.data) : legacyError(result);
    }
    const callLifecycleRoute = route.match(/^calls\/([0-9a-f-]{36})\/(accept|decline|cancel|end)$/i);
    if (callLifecycleRoute && (init?.method || 'GET').toUpperCase() === 'POST') {
      const [, callId, action] = callLifecycleRoute;
      const payload = action === 'end' ? parseBody(init) : {};
      const result = await platform(`/v1/sites/massage/calls/${encodeURIComponent(callId)}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(action === 'end' ? { reason: payload.reason || 'completed' } : {}),
      }, true);
      return result.data ? json(result.data) : legacyError(result);
    }
    const callSessionRoute = route.match(/^conversations\/(\d+)\/call-session$/);
    if (callSessionRoute) {
      const conversationId = await originalConversationId(Number(callSessionRoute[1]));
      if (!conversationId) return json({ code: 'CONVERSATION_NOT_FOUND', message: 'Không tìm thấy hội thoại.' }, 404);
      const result = await platform(`/v1/sites/massage/chat/conversations/${encodeURIComponent(conversationId)}/call-session`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ media: 'audio' }),
      }, true);
      return result.data ? json(result.data) : legacyError(result);
    }
    if (route === 'conversations' && (init?.method || 'GET').toUpperCase() === 'POST') {
      const payload = parseBody(init);
      const orders = await platform(payload.orderId ? `/v1/sites/massage/orders/${encodeURIComponent(String(payload.orderId))}` : '/v1/sites/massage/orders?page=1', {}, true);
      if (!orders.data) return legacyError(orders);
      const match = (payload.orderId ? [orders.data] : orders.data.items || []).find((item: any) =>
        payload.orderId ? String(item.id) === String(payload.orderId) : item.canContact === true && Number(item.partnerId) === Number(payload.partnerId) && Number(item.serviceId) === Number(payload.serviceId));
      if (!match) return json({ code: 'ORDER_NOT_FOUND', message: 'Chỉ mở tin nhắn sau khi đã có đơn với chuyên viên này.' }, 409);
      const result = await platform('/v1/sites/massage/chat/conversations', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ orderId: String(match.id) }),
      }, true);
      if (!result.data) return legacyError(result);
      const conversation = result.data.conversation || result.data;
      return json({ ...conversation, id: legacyConversationId(conversation.id), original_id: conversation.id });
    }
    if (route === 'conversations') {
      const result = await platform('/v1/sites/massage/chat/conversations', {}, true);
      if (!result.data) return legacyError(result);
      return json({ items: (result.data.items || []).map((item: any) => ({
        ...item, id: legacyConversationId(item.id), original_id: item.id,
        name: item.counterpartName || (item.kind === 'support' ? 'Tổng đài Tìm Massage' : 'Hội thoại dịch vụ'),
        avatar: item.counterpartAvatar || (item.kind === 'support' ? '/massage-assets/logo.png' : ''),
        service: item.serviceName || (item.kind === 'support' ? 'Hỗ trợ khách hàng' : 'Trao đổi dịch vụ'),
        preview: item.lastMessage || '', lastMessageAt: item.lastMessageAt || item.updatedAt,
        direction: item.selfRole === 'partner' ? 'store' : 'customer',
        role: item.kind === 'support' ? 'Hỗ trợ khách hàng' : (item.selfRole === 'partner' ? 'Khách tìm bạn' : 'Bạn tìm dịch vụ'),
        updated_at: item.updatedAt, unread_count: Number(item.unreadCount || 0), unread: Number(item.unreadCount || 0),
      })) });
    }
    const messageRoute = route.match(/^conversations\/(\d+)\/messages$/);
    if (messageRoute) {
      const conversationId = await originalConversationId(Number(messageRoute[1]));
      if (!conversationId) return json({ code: 'CONVERSATION_NOT_FOUND', message: 'Không tìm thấy hội thoại.' }, 404);
      if ((init?.method || 'GET').toUpperCase() === 'POST') {
        const payload = parseBody(init);
        if (payload.type && payload.type !== 'text') return json({ code: 'MESSAGE_TYPE_NOT_READY', message: 'Bản Node hiện chỉ nhận tin nhắn văn bản.' }, 422);
        const result = await platform(`/v1/sites/massage/chat/conversations/${encodeURIComponent(conversationId)}/messages`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clientId: payload.clientId || crypto.randomUUID(), text: String(payload.content || payload.text || '').trim() }),
        }, true);
        if (!result.data) return legacyError(result);
        const mapped = chatMessage(result.data.message);
        lastMessageByConversation.set(conversationId, String(result.data.message.id));
        return json(mapped);
      }
      const beforeLegacy = url.searchParams.get('before');
      const before = beforeLegacy ? messageFromLegacy.get(Number(beforeLegacy)) : undefined;
      const query = before ? `?before=${encodeURIComponent(before)}` : '';
      const result = await platform(`/v1/sites/massage/chat/conversations/${encodeURIComponent(conversationId)}/messages${query}`, {}, true);
      if (!result.data) return legacyError(result);
      const inbox = await platform('/v1/sites/massage/chat/conversations', {}, true);
      const conversation = (inbox.data?.items || []).find((item: any) => String(item.id) === String(conversationId)) || {};
      const items = [
        ...(result.data.items || []).map(chatMessage),
        ...(before ? [] : (result.data.calls || []).map(callHistoryMessage)),
      ].sort((left: any, right: any) => {
        const byTime = new Date(left.created_at).getTime() - new Date(right.created_at).getTime();
        return byTime || Number(left.id) - Number(right.id);
      });
      const latest = result.data.items?.at?.(-1) || result.data.items?.[result.data.items.length - 1];
      if (latest?.id) lastMessageByConversation.set(conversationId, String(latest.id));
      return json({
        ...result.data, items,
        nextBefore: result.data.nextBefore ? legacyMessageId(result.data.nextBefore) : null,
        counterpart: {
          name: conversation.counterpartName || (conversation.kind === 'support' ? 'Tổng đài Tìm Massage' : 'Hội thoại dịch vụ'),
          phone: '',
          service: conversation.serviceName || (conversation.kind === 'support' ? 'Hỗ trợ khách hàng' : 'Trao đổi dịch vụ'),
          avatar: conversation.counterpartAvatar || (conversation.kind === 'support' ? '/massage-assets/logo.png' : ''),
        },
        isBlocked: false, blockedByMe: false, canSend: true,
      });
    }
    const readRoute = route.match(/^conversations\/(\d+)\/read$/);
    if (readRoute) {
      const conversationId = await originalConversationId(Number(readRoute[1]));
      if (!conversationId) return json({ code: 'CONVERSATION_NOT_FOUND', message: 'Không tìm thấy hội thoại.' }, 404);
      const payload = parseBody(init);
      const originalMessageId = payload.messageId
        ? messageFromLegacy.get(Number(payload.messageId)) || String(payload.messageId)
        : lastMessageByConversation.get(conversationId);
      if (!originalMessageId) return json({ conversationId: Number(readRoute[1]), lastReadMessageId: null });
      const result = await platform(`/v1/sites/massage/chat/conversations/${encodeURIComponent(conversationId)}/read`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ messageId: originalMessageId }),
      }, true);
      return result.data ? json({ conversationId: Number(readRoute[1]), lastReadMessageId: legacyMessageId(result.data.lastReadMessageId) }) : legacyError(result);
    }
    if (route === 'categories') {
      const result = await platform('/v1/sites/massage/home');
      return result.data ? json((result.data.categories || []).map(category)) : json(result.error, result.response.status);
    }
    if (route === 'store' && (init?.method || 'GET').toUpperCase() === 'GET') {
      const result = await ownerStore(url.searchParams.get('partner_id') || '');
      return result.data ? json(result.data) : legacyError(result);
    }
    if (route === 'partner-application') {
      const method = (init?.method || 'GET').toUpperCase();
      if (method === 'GET') {
        const result = await platform('/v1/sites/massage/owner/application', {}, true);
        return result.data ? json(result.data) : legacyError(result);
      }
      const payload = parseBody(init);
      const result = await platform('/v1/sites/massage/owner/application', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payload, submit: payload.submit === true, consent: payload.consent === true }),
      }, true);
      return result.data ? json(result.data) : legacyError(result);
    }
    if (route === 'store' && ['PATCH', 'PUT'].includes((init?.method || 'GET').toUpperCase())) {
      const payload = parseBody(init);
      const isMediaSelection = Object.prototype.hasOwnProperty.call(payload, 'avatarId') || Object.prototype.hasOwnProperty.call(payload, 'coverIds') || Object.prototype.hasOwnProperty.call(payload, 'albumIds');
      const result = isMediaSelection ? await saveOwnerMediaSelection(payload, url.searchParams.get('partner_id') || '') : await saveOwnerStore(payload, url.searchParams.get('partner_id') || '');
      return result.data ? json(result.data) : legacyError(result);
    }
    if (route === 'store/order-availability' && (init?.method || 'GET').toUpperCase() === 'PATCH') {
      const payload = parseBody(init);
      const result = await saveOwnerAccepting(asFlag(payload.acceptOrders), url.searchParams.get('partner_id') || '');
      return result.data ? json({ partnerId: Number(result.data.id), acceptOrders: asFlag(result.data.accepting) }) : legacyError(result);
    }
    if (route === 'media' && (init?.method || 'GET').toUpperCase() === 'POST' && init?.body instanceof FormData) {
      const result = await uploadOwnerMedia(init.body);
      return result.data ? json(result.data) : legacyError(result);
    }
    if (route === 'services' && (init?.method || 'GET').toUpperCase() === 'GET') {
      const result = await ownerStore(url.searchParams.get('partner_id') || '');
      return result.data ? json(result.data.services || []) : legacyError(result);
    }
    if (route === 'services' && (init?.method || 'GET').toUpperCase() === 'POST') {
      const result = await saveOwnerService(parseBody(init));
      return result.data ? json(result.data, 201) : legacyError(result);
    }
    const ownerServiceRoute = route.match(/^services\/(\d+)$/);
    if (ownerServiceRoute && ['PATCH','PUT'].includes((init?.method || 'GET').toUpperCase())) {
      const result = await saveOwnerService(parseBody(init), ownerServiceRoute[1]);
      return result.data ? json(result.data) : legacyError(result);
    }
    if (ownerServiceRoute && (init?.method || 'GET').toUpperCase() === 'DELETE') {
      const result = await platform(`/v1/sites/massage/owner/services/${encodeURIComponent(ownerServiceRoute[1])}`, { method: 'DELETE' }, true);
      if (result.data) ownerStorePromise = null;
      return result.data ? json(result.data) : legacyError(result);
    }
    if (route === 'areas') return json([
      { id: 10, slug: 'tp-ho-chi-minh', name: 'TP. Hồ Chí Minh', parent: 0 },
      { id: 20, slug: 'vung-tau', name: 'Vũng Tàu', parent: 0 },
      { id: 30, slug: 'binh-duong', name: 'Bình Dương', parent: 0 },
      { id: 40, slug: 'ha-noi', name: 'Hà Nội', parent: 0 },
      { id: 50, slug: 'hai-phong', name: 'Hải Phòng', parent: 0 },
      { id: 60, slug: 'da-nang', name: 'Đà Nẵng', parent: 0 },
      { id: 70, slug: 'nha-trang', name: 'Nha Trang', parent: 0 },
      { id: 80, slug: 'da-lat', name: 'Đà Lạt', parent: 0 },
      { id: 90, slug: 'bac-ninh', name: 'Bắc Ninh', parent: 0 },
      { id: 100, slug: 'hue', name: 'Huế', parent: 0 },
      { id: 110, slug: 'quang-nam', name: 'Quảng Nam', parent: 0 },
    ]);
    if (route === 'random-specialist/catalog') {
      const result = await platform('/v1/sites/massage/random-specialist/catalog');
      return result.data ? json(result.data) : json(result.error, result.response.status);
    }
    if (route === 'partners') {
      const page = Math.max(1, Number(url.searchParams.get('page') || 1));
      const areaLabels: Record<string,string> = {
        'tp-ho-chi-minh':'Hồ Chí Minh','vung-tau':'Vũng Tàu','binh-duong':'Bình Dương',
        'ha-noi':'Hà Nội','hai-phong':'Hải Phòng','da-nang':'Đà Nẵng','nha-trang':'Nha Trang',
        'da-lat':'Đà Lạt','bac-ninh':'Bắc Ninh','hue':'Huế','quang-nam':'Quảng Nam',
      };
      const area = new URLSearchParams(location.search).get('area') || '';
      const query = new URLSearchParams({ page: String(page), search: url.searchParams.get('search') || areaLabels[area] || '' });
      for (const key of ['per_page','service','mode','price_max','rating_min','lat','lng','sort']) {
        const value = url.searchParams.get(key);
        if (value) query.set(key, value);
      }
      const result = await platform(`/v1/sites/massage/partners?${query}`);
      return result.data ? json({ ...result.data, items: (result.data.items || []).map(partner) }) : json(result.error, result.response.status);
    }
    if (route === 'favorites') {
      const page = Math.max(1, Number(url.searchParams.get('page') || 1));
      const result = await platform(`/v1/sites/massage/favorites?page=${page}`, {}, true);
      return result.data ? json({ ...result.data, items: (result.data.items || []).map(partner) }) : legacyError(result);
    }
    if (/^partners\/\d+\/reviews$/.test(route)) {
      const partnerId=route.split('/')[1];
      if ((init?.method || 'GET').toUpperCase() === 'POST') {
        const payload=parseBody(init);
        const result=await platform(`/v1/sites/massage/partners/${partnerId}/reviews`,{
          method:'POST',headers:{'Content-Type':'application/json'},
          body:JSON.stringify({rating:Number(payload.rating),content:String(payload.text||payload.content||'').trim()}),
        },true);
        return result.data?json(result.data,result.response.status):legacyError(result);
      }
      const page=Math.max(1,Number(url.searchParams.get('page')||1));
      const result=await platform(`/v1/sites/massage/partners/${partnerId}/reviews?page=${page}`);
      if (!result.data) return legacyError(result);
      const items=(result.data.items||[]).map((item:any)=>({
        id:item.id,rating:Number(item.rating||0),author:item.author||'Khách hàng Tìm',
        avatar:'/massage-assets/logo.png',review_text:item.content||'',media:[],created_at:item.createdAt||'',
      }));
      return json({...result.data,items,total:items.length});
    }
    const availabilityRoute = route.match(/^partners\/(\d+)\/availability$/);
    if (availabilityRoute) {
      const serviceId = url.searchParams.get('service_id') || '';
      if (!/^\d+$/.test(serviceId)) return json({ code: 'SERVICE_REQUIRED', message: 'Chưa xác định được dịch vụ.' }, 422);
      const result = await platform(`/v1/sites/massage/services/${serviceId}/slots`);
      if (!result.data) return legacyError(result);
      const groups = new Map<string, Array<{time:string;slotId:number}>>();
      for (const item of result.data.items || []) {
        const value = vietnamParts(item.startsAt); const slots = groups.get(value.date) || [];
        slots.push({ time: value.time, slotId: Number(item.id) }); groups.set(value.date, slots);
      }
      const days = [...groups.entries()].sort(([a],[b]) => a.localeCompare(b)).map(([date,slots]) => ({
        date, slots: slots.sort((a,b) => a.time.localeCompare(b.time)),
      }));
      return json({ schedule: { days }, partnerId: Number(availabilityRoute[1]), serviceId: Number(serviceId) });
    }
    const favoriteRoute = route.match(/^partners\/(\d+)\/favorite$/);
    if (favoriteRoute) {
      const method = (init?.method || 'GET').toUpperCase();
      const result = await platform(`/v1/sites/massage/partners/${favoriteRoute[1]}/favorite`, { method }, true);
      return result.data ? json(result.data, result.response.status) : legacyError(result);
    }
    const contactRoute = route.match(/^partners\/(\d+)\/contact$/);
    if (contactRoute) {
      const payload = parseBody(init);
      const result = await platform(`/v1/sites/massage/partners/${contactRoute[1]}/contact`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: String(payload.type || '') }),
      }, true);
      return result.data ? json(result.data, result.response.status) : legacyError(result);
    }
    const reportRoute = route.match(/^partners\/(\d+)\/report$/);
    if (reportRoute) {
      const payload = parseBody(init);
      const result = await platform(`/v1/sites/massage/partners/${reportRoute[1]}/report`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason: String(payload.reason || '').trim() }),
      }, true);
      return result.data ? json(result.data, result.response.status) : legacyError(result);
    }
    if (route === 'orders' && (init?.method || 'GET').toUpperCase() === 'POST') {
      const payload = parseBody(init);
      if (payload.randomProductId || payload.randomVariantId) {
        const latitude = optionalCoordinate(payload.lat, -90, 90);
        const longitude = optionalCoordinate(payload.lng, -180, 180);
        if (latitude === null || longitude === null) {
          return json({ code: 'LOCATION_REQUIRED', message: 'Hãy chọn vị trí chính xác trên bản đồ trước khi đặt đơn.' }, 422);
        }
        const result = await platform('/v1/sites/massage/random-specialist/orders', {
          method:'POST',headers:{'Content-Type':'application/json',...customerBrowserHeaders},body:JSON.stringify({clientId:stableClientId(payload.clientRequestId),name:String(payload.name||'').trim(),phone:String(payload.phone||'').trim(),randomProductId:String(payload.randomProductId||''),randomVariantId:String(payload.randomVariantId||''),date:String(payload.date||''),time:String(payload.time||'').slice(0,5),address:String(payload.address||'').trim(),latitude,longitude,note:String(payload.note||'').trim()}),
        }, Boolean(readCustomerSession()));
        if(!result.data)return legacyError(result);if(result.data.session?.accessToken)writeCustomerSession(result.data.session);return json(legacyOrder(result.data.order||result.data),result.response.status);
      }
      const serviceId = String(payload.serviceId || '');
      const latitude = optionalCoordinate(payload.lat, -90, 90);
      const longitude = optionalCoordinate(payload.lng, -180, 180);
      const coordinatesSupplied = payload.lat !== null && payload.lat !== undefined && payload.lat !== ''
        || payload.lng !== null && payload.lng !== undefined && payload.lng !== '';
      if (coordinatesSupplied && (latitude === null || longitude === null)) {
        return json({ code: 'LOCATION_REQUIRED', message: 'Vị trí phục vụ không hợp lệ. Hãy chọn lại vị trí trên bản đồ.' }, 422);
      }
      const slots = await platform(`/v1/sites/massage/services/${encodeURIComponent(serviceId)}/slots`);
      if (!slots.data) return legacyError(slots);
      const selected = (slots.data.items || []).find((item: any) => {
        const value = vietnamParts(item.startsAt); return value.date === String(payload.date) && value.time === String(payload.time).slice(0,5);
      });
      if (!selected) return json({ code: 'SLOT_UNAVAILABLE', message: 'Khung giờ này vừa hết chỗ hoặc không còn khả dụng. Vui lòng chọn giờ khác.' }, 409);
      const result = await platform('/v1/sites/massage/orders', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...customerBrowserHeaders },
        body: JSON.stringify({
          clientId: stableClientId(payload.clientRequestId), name:String(payload.name||'').trim(), phone:String(payload.phone||'').trim(), serviceId, slotId: String(selected.id),
          mode: payload.place === 'Tại cửa hàng' ? 'at_store' : 'mobile',
          address: String(payload.address || '').trim() || 'Địa chỉ cửa hàng',
          ...(latitude !== null && longitude !== null ? { latitude, longitude } : {}),
          mapUrl:String(payload.mapUrl||''),note: String(payload.note || '').trim(),
        }),
      }, Boolean(readCustomerSession()));
      if (!result.data) return legacyError(result);
      if (result.data.session?.accessToken) writeCustomerSession(result.data.session);
      return json(legacyOrder(result.data.order || result.data), result.response.status);
    }
    if (route === 'orders') {
      const page = Math.max(1, Number(url.searchParams.get('page') || 1)); const scope = url.searchParams.get('scope') || 'mine';
      if (!readCustomerSession()) return json({ items: [], page, hasMore: false, forbidden: scope === 'area' });
      const result = await platform(`/v1/sites/massage/orders?page=${page}`, {}, true);
      if (!result.data) return legacyError(result);
      const actor = readCustomerSession()?.globalUserId;
      const perspective = scope === 'area' ? 'provider' : 'customer';
      const items = (result.data.items || []).filter((item: any) => scope === 'area' ? item.ownerId === actor : item.customerId === actor).map((item: any) => legacyOrder(item, perspective));
      return json({ ...result.data, items, forbidden: false });
    }
    if (route === 'place-suggestions') {
      const query = String(url.searchParams.get('q') || '').trim();
      if (query.length < 2) return json({ items: [] });
      const params = new URLSearchParams({ input: query });
      const lat = Number(url.searchParams.get('lat')), lng = Number(url.searchParams.get('lng'));
      if (Number.isFinite(lat) && Number.isFinite(lng)) { params.set('lat', String(lat)); params.set('lng', String(lng)); }
      const result = await platform(`/v1/sites/massage/places/autocomplete?${params}`);
      if (!result.data) return legacyError(result);
      const items = (result.data.predictions || []).map((item: any) => ({
        description: String(item.description || ''),
        label: String(item.structured_formatting?.main_text || item.description || ''),
        secondary: String(item.structured_formatting?.secondary_text || ''),
        placeId: String(item.place_id || ''),
      })).filter((item: any) => item.description);
      return json({ items });
    }
    if (route === 'geocode' && (init?.method || 'GET').toUpperCase() === 'POST') {
      const payload = parseBody(init);
      let action = 'geocode'; const params = new URLSearchParams();
      if (payload.place_id) { action = 'detail'; params.set('place_id', String(payload.place_id)); }
      else if (Number.isFinite(Number(payload.lat)) && Number.isFinite(Number(payload.lng))) { action = 'reverse'; params.set('lat', String(Number(payload.lat))); params.set('lng', String(Number(payload.lng))); }
      else params.set('address', String(payload.address || '').trim());
      const result = await platform(`/v1/sites/massage/places/${action}?${params}`);
      if (!result.data) return legacyError(result);
      const source = action === 'detail' ? result.data.result : (result.data.results || [])[0];
      const location = source?.geometry?.location;
      if (!source || !Number.isFinite(Number(location?.lat)) || !Number.isFinite(Number(location?.lng))) return json({ code:'PLACE_NOT_FOUND', message:'Không xác định được vị trí này.' },404);
      return json({ address:String(source.formatted_address || source.description || payload.address || ''), lat:Number(location.lat), lng:Number(location.lng), placeId:String(source.place_id || payload.place_id || '') });
    }
    const orderDetailRoute = route.match(/^orders\/(\d+)$/);
    if (orderDetailRoute) {
      const result = await platform(`/v1/sites/massage/orders/${orderDetailRoute[1]}`, {}, true);
      const perspective = result.data?.customerId === readCustomerSession()?.globalUserId ? 'customer' : 'provider';
      return result.data ? json(legacyOrder(result.data, perspective)) : legacyError(result);
    }
    const selectApplicantRoute=route.match(/^orders\/(\d+)\/select-applicant$/);
    if(selectApplicantRoute){
      const payload=parseBody(init);const result=await platform(`/v1/sites/massage/orders/${selectApplicantRoute[1]}/select-applicant`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({applicantId:String(payload.applicantId||'')})},true);
      return result.data?json(legacyOrder(result.data)):legacyError(result);
    }
    const orderActionRoute = route.match(/^orders\/(\d+)\/(cancel|reject|confirm|receive|start-travel|meet-customer|complete)$/);
    if (orderActionRoute) {
      const [,id,action] = orderActionRoute;
      if(action==='receive'){
        const detail=await platform(`/v1/sites/massage/orders/${id}`,{},true);
        if(!detail.data)return legacyError(detail);
        orderVersions.set(id,Number(detail.data.version));
        if(detail.data.status==='matching'){
          const claimed=await platform(`/v1/sites/massage/orders/${id}/claim`,{method:'POST'},true);
          return claimed.data?json(legacyOrder(claimed.data,'provider')):legacyError(claimed);
        }
      }
      const targets: Record<string,string> = { cancel:'cancelled',reject:'rejected',confirm:'confirmed',receive:'confirmed','start-travel':'travelling','meet-customer':'met_customer',complete:'completed' };
      let version = orderVersions.get(id);
      if (!version) {
        const detail = await platform(`/v1/sites/massage/orders/${id}`, {}, true); if (!detail.data) return legacyError(detail);
        version = Number(detail.data.version || 1);
      }
      const result = await platform(`/v1/sites/massage/orders/${id}/actions`, {
        method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({expectedVersion:version,status:targets[action]}),
      },true);
      return result.data ? json(legacyOrder(result.data)) : legacyError(result);
    }
    const partnerCoverageRoute = route.match(/^partners\/(\d+)\/coverage$/);
    if (partnerCoverageRoute) {
      const serviceId = url.searchParams.get('serviceId') || '';
      const lat = url.searchParams.get('lat') || '';
      const lng = url.searchParams.get('lng') || '';
      const result = await platform(`/v1/sites/massage/partners/${partnerCoverageRoute[1]}/coverage?serviceId=${encodeURIComponent(serviceId)}&lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`);
      return result.data ? json(result.data) : legacyError(result);
    }
    if (route.startsWith('partners/')) {
      const key = decodeURIComponent(route.slice('partners/'.length))
        .split(/[/?#]/, 1)[0]
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, '');
      if (!key) return json({ code: 'PARTNER_NOT_FOUND', message: 'Không xác định được hồ sơ chuyên viên.' }, 404);
      const endpoint = /^\d+$/.test(key)
        ? `/v1/sites/massage/partners/${key}`
        : `/v1/sites/massage/partners/by-slug/${encodeURIComponent(key)}`;
      const result = await platform(endpoint);
      if (!result.data) {
        const notFound=result.response.status===404||result.error?.code==='PARTNER_NOT_FOUND';
        return json(notFound?{code:'PARTNER_NOT_FOUND',message:'Không tìm thấy chuyên viên hoặc gian hàng này.'}:result.error,result.response.status);
      }
      const mapped = partner(result.data);
      if (readCustomerSession()) {
        const favorite = await platform(`/v1/sites/massage/partners/${mapped.id}/favorite`, {}, true);
        if (favorite.data) mapped.saved = Boolean(favorite.data.saved);
      }
      return json(mapped);
    }
    if (route === 'auth/otp/status') {const result=await platform('/v1/auth/otp/status?tenantId=massage');return result.data?json({...result.data,provider:'node'}):legacyError(result);}
    return json({ code: 'NOT_IMPLEMENTED', message: 'Chức năng đang được chuyển sang nền tảng mới.' }, 501);
  };
}
